import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { UpdateProfileInput, UserProfile } from '../types/profile';

const USERNAME_MAX_LENGTH = 32;

const normalizeUsername = (value: string) => {
  const normalized = value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized.slice(0, USERNAME_MAX_LENGTH);
};

const buildFallbackUsername = (user: User) => {
  const displayName = typeof user.user_metadata?.display_name === 'string'
    ? user.user_metadata.display_name
    : '';
  const emailName = typeof user.email === 'string'
    ? user.email.split('@')[0]
    : '';

  const candidate = normalizeUsername(displayName || emailName || 'user');
  return candidate || `user_${user.id.slice(0, 8)}`;
};

const isNotFoundError = (message: string | undefined) => {
  if (!message) {
    return false;
  }

  return message.toLowerCase().includes('no rows');
};

export const getProfileByUserId = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle<UserProfile>();

  return { data, error };
};

export const getProfileByUsername = async (username: string) => {
  const normalizedUsername = normalizeUsername(username);
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('username', normalizedUsername)
    .maybeSingle<UserProfile>();

  return { data, error };
};

export const updateMyProfile = async (userId: string, updates: UpdateProfileInput) => {
  const payload: UpdateProfileInput = {
    ...updates,
  };

  if (typeof payload.username === 'string') {
    payload.username = normalizeUsername(payload.username);
  }

  const { data, error } = await supabase
    .from('profiles')
    .update(payload)
    .eq('user_id', userId)
    .select('*')
    .single<UserProfile>();

  return { data, error };
};

export const ensureMyProfile = async (user: User) => {
  const existing = await getProfileByUserId(user.id);
  if (existing.error) {
    return { data: null, error: existing.error };
  }

  if (existing.data) {
    return { data: existing.data, error: null };
  }

  const baseUsername = buildFallbackUsername(user);

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const suffix = attempt === 0 ? '' : `_${Math.floor(Math.random() * 9000) + 1000}`;
    const candidate = `${baseUsername}${suffix}`.slice(0, USERNAME_MAX_LENGTH);

    const { data, error } = await supabase
      .from('profiles')
      .insert({
        user_id: user.id,
        username: candidate,
        biography: null,
        age: null,
        sex: null,
        avatar_url: typeof user.user_metadata?.avatar_url === 'string' ? user.user_metadata.avatar_url : null,
      })
      .select('*')
      .single<UserProfile>();

    if (!error) {
      return { data, error: null };
    }

    if (!isNotFoundError(error.message) && !error.message.toLowerCase().includes('duplicate key value')) {
      return { data: null, error };
    }
  }

  return {
    data: null,
    error: {
      message: 'Could not create profile. Please try again.',
    },
  };
};
